import { Link } from 'react-router-dom';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import { buildReportingBetaHref } from '../../utils/build-reporting-beta-href';
import PacingBoardSparkline from './pacing-board-sparkline';
import PacingBoardStatusBadge from './pacing-board-status-badge';
import type { PacingBudget } from '../../types/pacing-board.types';

function formatCurrency(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num || 0);
}

function formatAmountPerDay(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num || 0);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface PacingBudgetHeroProps {
  budget: PacingBudget | null;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export default function PacingBudgetHero({ budget, canEdit, onEdit, onDelete }: PacingBudgetHeroProps) {
  if (!budget) {
    return (
      <div className="flex flex-1 items-center justify-center bg-[#F4F5F8] text-sm text-[#9CA3AF]">
        Select a budget from the rail to view details
      </div>
    );
  }

  const detail = budget.detail;
  const reportingHref =
    budget.is_linked && budget.linked_campaign
      ? buildReportingBetaHref({
          platform: budget.linked_campaign.platform,
          campaignId: budget.linked_campaign.ext_campaign_id,
          campaignName: budget.linked_campaign.title,
          startDate: budget.start_date,
          endDate: budget.end_date,
        })
      : null;

  return (
    <div className="flex flex-1 flex-col overflow-y-auto bg-[#F4F5F8] p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className={portalDs.sectionTitle}>{budget.campaign_name}</h2>
          <p className="mt-1 text-sm text-[#5E6278]">
            {budget.client_name_display || 'No client'} · {formatDate(budget.start_date)} – {formatDate(budget.end_date)}
            {reportingHref && budget.linked_campaign && (
              <span className="ml-2">
                · Linked to{' '}
                <Link
                  to={reportingHref}
                  className="font-medium text-[#2563EB] underline-offset-2 hover:underline"
                >
                  {budget.linked_campaign.title}
                </Link>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PacingBoardStatusBadge status={detail?.status} />
          {canEdit && (
            <>
              <PortalDsButton variant="secondary" size="sm" onClick={onEdit}>
                Edit
              </PortalDsButton>
              <PortalDsButton variant="ghost" size="sm" onClick={onDelete}>
                Delete
              </PortalDsButton>
            </>
          )}
        </div>
      </div>

      {detail?.alert_text && (
        <div className="mb-6 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
          {detail.alert_text}
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard label="Actual spend" value={formatCurrency(detail?.actual_spend)} />
        <MetricCard label="Ideal to date" value={formatCurrency(detail?.ideal_to_date)} />
        <MetricCard label="Variance" value={detail ? `${detail.variance.toFixed(1)}%` : '—'} />
        <MetricCard label="Projected total" value={formatCurrency(detail?.projected_total)} />
        <MetricCard label="Gross budget" value={formatCurrency(budget.gross_budget)} />
        <MetricCard
          label="Margin"
          value={`${parseFloat(budget.margin_pct).toFixed(1)}%`}
        />
        <MetricCard label="Net budget" value={formatCurrency(budget.net_budget)} />
        <MetricCard label="Days left" value={String(detail?.days_left ?? '—')} />
        <MetricCard label="Elapsed days" value={String(detail?.elapsed_days ?? '—')} />
        <MetricCard label="Amt per day" value={formatAmountPerDay(detail?.amount_per_day)} />
      </div>

      <PacingBoardSparkline sparkline={detail?.sparkline} />
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className={`${portalDs.card} ${portalDs.cardPadding}`}>
      <div className={portalDs.sectionLabel}>{label}</div>
      <div className="mt-1 text-xl font-bold tracking-[-0.5px] text-[#0D0F1A]">{value}</div>
    </div>
  );
}
